import apiClient from './apiClient.js';

export const shopService = {
  /**
   * Get all shops with pagination
   */
  getShops: async (params = {}) => {
    const response = await apiClient.get('/shops', { params });
    return response.data;
  },

  /**
   * Get shop statistics
   */
  getShopStats: async () => {
    const response = await apiClient.get('/shops/stats');
    return response.data;
  },

  /**
   * Get shop by ID
   */
  getShopById: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}`);
    return response.data;
  },

  /**
   * Get shop by locationId (from parent system)
   */
  getShopByLocationId: async (locationId) => {
    const response = await apiClient.get(`/shops/by-location/${locationId}`);
    return response.data;
  },

  /**
   * Create a new shop
   */
  createShop: async (shopData) => {
    const response = await apiClient.post('/shops', shopData);
    return response.data;
  },

  /**
   * Update shop
   */
  updateShop: async (shopId, shopData) => {
    const response = await apiClient.put(`/shops/${shopId}`, shopData);
    return response.data;
  },

  /**
   * Delete shop
   */
  deleteShop: async (shopId) => {
    const response = await apiClient.delete(`/shops/${shopId}`);
    return response.data;
  },

  /**
   * Geocode shop address (USES MELISSA CREDITS!)
   */
  geocodeShop: async (shopId) => {
    const response = await apiClient.post(`/shops/${shopId}/geocode`);
    return response.data;
  },

  /**
   * Update shop radius. `clientId` (optional) lets the backend skip echoing
   * the resulting `shop-updated` SSE event back to the caller.
   */
  updateShopRadius: async (shopId, radiusMiles, clientId) => {
    const response = await apiClient.patch(`/shops/${shopId}/radius`, {
      radiusMiles,
      ...(clientId ? { clientId } : {}),
    });
    return response.data;
  },

  /**
   * How many postcards this shop can still mail (Service Intel's
   * NcaPostcardsPurchasedQty, cached + decremented locally as batches are made).
   * Costs nothing — no Melissa credits, and it only calls Service Intel when the
   * cached balance is stale or zero.
   */
  getPostcardQuota: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}/postcard-quota`);
    return response.data;
  },

  /**
   * Force a re-read of the balance from Service Intel. Use it when the client
   * just purchased more postcards and shouldn't have to wait for the cache TTL.
   */
  refreshPostcardQuota: async (shopId) => {
    const response = await apiClient.post(`/shops/${shopId}/postcard-quota/refresh`);
    return response.data;
  },

  /**
   * Search addresses within shop radius (USES MELISSA CREDITS!)
   */
  searchAddresses: async (shopId, options = {}) => {
    const response = await apiClient.post(`/shops/${shopId}/addresses/search`, options);
    return response.data;
  },

  /**
   * Search addresses by location (ZIP, city, state) (USES MELISSA CREDITS!)
   */
  searchAddressesByLocation: async (shopId, options = {}) => {
    const response = await apiClient.post(`/shops/${shopId}/addresses/search-by-location`, options);
    return response.data;
  },

  /**
   * Get saved addresses for shop
   */
  getAddresses: async (shopId, params = {}) => {
    const response = await apiClient.get(`/shops/${shopId}/addresses`, { params });
    return response.data;
  },

  /**
   * Get address statistics for shop
   */
  getAddressStats: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}/addresses/stats`);
    return response.data;
  },

  /**
   * Get batches for shop
   */
  getBatches: async (shopId, params = {}) => {
    const response = await apiClient.get(`/shops/${shopId}/batches`, { params });
    return response.data;
  },

  /**
   * Get batch statistics for shop
   */
  getBatchStats: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}/batches/stats`);
    return response.data;
  },

  /**
   * Create batch from search results
   */
  createBatchFromSearch: async (shopId, data) => {
    const response = await apiClient.post(`/shops/${shopId}/batches/from-search`, data);
    return response.data;
  },

  /**
   * Get batch addresses for map display
   */
  getBatchAddressesForMap: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}/batches/map-addresses`);
    return response.data;
  },

  /**
   * Get existing customers (pushed by Service Intel) for map display
   */
  getCustomersForMap: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}/customers/map-addresses`);
    return response.data;
  },
};

export default shopService;
